/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
/* eslint-disable no-underscore-dangle */
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useReducer,
  ReactElement,
  SyntheticEvent,
} from "react";
import axios, { Method } from "axios";
import styled from "styled-components";
import { jQueryTooltipContainer } from "../../../interfaces/interfaces";

import setUpAxios from "../../../helperFunctions/helperFunctions";
import {
  handleGetFormStateData,
  getAwardApproversOptions,
  validate,
} from "./lib/utils";
import {
  initialState,
  init,
  approversReducer,
  errorsInitialState,
  errorsReducer,
  errorsInit,
  ApproversResponse,
} from "./lib/approversReducers";
import AutomaticApprovalToggle from "./components/AutomaticApprovalToggle";
import Levels from "./components/Levels";
import PendingAwardUpdates from "./components/PendingAwardUpdates";

export type Option = {
  label: string;
  value: string;
};

type Level = {
  allowed_approver_ids: string[];
};

type Award = {
  award: {
    _id: string;
  };
  approval_process?: {
    _id: string;
    levels: Level[];
  };
};

type AwardApprovers = [Array<[string, string]>];
interface ApproversTabProps {
  award: Award;
  editing: boolean;
  award_approvers: AwardApprovers;
  approver_form_data: Array<ApproversResponse[]>;
  claimable: boolean;
  pending_requests: string;
  invalid_approval_process: boolean;
}

const StyledTabWrapper = styled.div`
  padding: 10px 20px;
`;

function ApproversTab(props: ApproversTabProps): ReactElement {
  const claimableAwardForm =
    document.querySelector("#new_claimable_award_form form") ||
    document.querySelector("#edit_claimable_award_form form");
  const manualAwardForm =
    document.querySelector("#new_award_form form") ||
    document.querySelector("#edit_award_form form");
  const awardForm = props.claimable ? claimableAwardForm : manualAwardForm;
  const submitButton: HTMLButtonElement = awardForm.querySelector(
    ".award-modal-confirm-btn"
  );
  const formRef = useRef(awardForm);
  const [options, setOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorState, errorsDispatch] = useReducer(
    errorsReducer,
    errorsInitialState,
    errorsInit
  );
  const [touched, setTouched] = useState({});
  const [levelState, levelsDispatch] = useReducer(
    approversReducer,
    initialState,
    () => {
      return init(props.approver_form_data || [], setTouched);
    }
  );
  const isNew = !props.editing;
  const noApprovalProcess = !props.award?.approval_process?.levels?.length;
  const isAutomaticallyApproved = isNew || noApprovalProcess;
  const [approvedAutomatically, setApproveAutomatically] = useState(
    isAutomaticallyApproved
  );
  const [resetPendingRequests, setResetPendingRequests] = useState(
    props.invalid_approval_process
  );

  const handleAddLevel = (): void => {
    submitButton.disabled = true;
    levelsDispatch({
      type: "ADD_LEVEL",
    });

    setTouched((prevState) => {
      const levelLength = Object.keys(prevState).length;
      return {
        ...prevState,
        [`level_${levelLength + 1}`]: false,
      };
    });
  };

  const handleClearLevel = (): void => {
    levelsDispatch({
      type: "CLEAR_LEVELS",
    });
  };

  const handleRemoveLevel = (position: number): void => {
    errorsDispatch({
      type: "REMOVE_ERROR",
      name: `level_${position}`,
      payload: null,
    });

    levelsDispatch({
      type: "REMOVE_LEVEL",
      index: position - 1,
    });

    setTouched((prevState) => {
      // don't modify state directly.
      const deleteLevel = { ...prevState };
      // delete level in question
      delete deleteLevel[`level_${position}`];

      const renamedLevels = {};
      // ensure levels are sequential
      Object.keys(deleteLevel).forEach((touchedLevel, index) => {
        renamedLevels[`level_${index + 1}`] = prevState[touchedLevel];
      });
      return renamedLevels;
    });
  };

  const handleApproveAutomaticallyChange = (checked: boolean): void => {
    setApproveAutomatically(checked);

    if (!checked && !levelState.levels.length) {
      handleAddLevel();
    } else if (checked) {
      handleClearLevel();
      submitButton.disabled = false;
    }
  };

  const handleChange = (selectedOptions: Option[], position: number): void => {
    if (!selectedOptions || !selectedOptions.length) {
      // selectedOptions is null when the select is cleared but there's no action fired
      // so we have to watch for the change here instead of handleInputChange
      const error = validate.level({
        name: `level_${position}`,
        selectedOptions,
      });

      errorsDispatch({
        type: "REMOVE_ERROR",
        name: `level_${position}`,
        payload: error,
      });

      if (error) {
        errorsDispatch({
          type: "ADD_ERROR",
          name: `level_${position}`,
          payload: error,
        });
      }
    }

    levelsDispatch({
      payload: selectedOptions,
      type: "UPDATE_SELECTED_OPTIONS",
      index: position - 1,
    });
  };

  const handleInputChange = (
    value: Option[],
    position: number,
    action: {
      action: string;
    }
  ): void => {
    const error = validate.level({ name: `level_${position}`, value });
    const actionsToWatch = ["input-blur", "menu-close"];
    const blurOrClose = actionsToWatch.includes(action.action);

    if (!value?.length && blurOrClose) {
      errorsDispatch({
        type: "REMOVE_ERROR",
        name: `level_${position}`,
        payload: error,
      });

      if (error) {
        errorsDispatch({
          type: "ADD_ERROR",
          name: `level_${position}`,
          payload: error,
        });
      }
    } else if (value && value.length && blurOrClose) {
      errorsDispatch({
        type: "REMOVE_ERROR",
        name: `level_${position}`,
        payload: null,
      });
    }
  };

  const handleBlur = (e: SyntheticEvent, name: string): void => {
    setTouched((prevState) => {
      return {
        ...prevState,
        [name]: true,
      };
    });
  };

  const handleFormSubmit = useCallback(
    async (e: Event, method: Method, url: string): Promise<void> => {
      setSubmitting(true);
      e.preventDefault();
      setUpAxios();
      const formData = e.target as HTMLFormElement;
      const formState = {};
      const formArray = Array.from(formData.elements);

      formArray.forEach((element: HTMLInputElement) => {
        let formValue;
        if (element.files?.length) {
          formValue = element.files;
        }
        // this is cursed code. Temporary until we can reactify the award modal
        // this prevents existing images from being deleted when editing awards
        else if (element.name === "award[user][remove_avatar]") {
          formValue = element.checked ? element.value : null;
        } else if (element.name === "bonus_privacy_changeable") {
          formValue = element.checked ? true : false;
        } else if (element.name === "require_proof") {
          formValue = element.checked ? true : false;
        } else {
          formValue = element.value;
        }
        formState[element.name] = formValue;
      }, []);

      const params = await handleGetFormStateData(
        formState,
        approvedAutomatically,
        resetPendingRequests,
        levelState.levels,
        props.editing,
        props.claimable,
        props.award.approval_process?._id
      );

      const response = await axios.request({
        method,
        url,
        data: params,
      });

      if (response.status >= 200 && response.status < 400) {
        window.location.reload();
      }
      setSubmitting(false);
    },
    [
      approvedAutomatically,
      levelState.levels,
      props.editing,
      props.claimable,
      props.award.approval_process?._id,
      resetPendingRequests,
    ]
  );

  useEffect(() => {
    const tooltipContainer = $(
      '[data-toggle="tooltip"]'
    ) as jQueryTooltipContainer;
    tooltipContainer.tooltip();
  }, []);

  useEffect(() => {
    submitButton.disabled = false;
    const hasEmptyInput = levelState.levels.some((level) => {
      return level?.length === 0;
    });
    const hasLevelValidationErrors = validate.levels(errorState.errors);
    const badLevels = validate.levels_approvers(levelState);
    if (
      !approvedAutomatically &&
      (hasLevelValidationErrors ||
        hasEmptyInput ||
        (props.invalid_approval_process && badLevels))
    ) {
      submitButton.disabled = true;
    }
  }, [errorState, submitButton, levelState, props.invalid_approval_process]);

  useEffect(() => {
    const approverOptions = getAwardApproversOptions(props.award_approvers);
    setOptions(approverOptions);
  }, [props.award_approvers]);

  useEffect(() => {
    const listenForSubmit = props.editing
      ? (e: Event): Promise<void> => {
          return handleFormSubmit(
            e,
            "patch",
            `/admin/awards/${props.award.award._id}`
          );
        }
      : (e: Event): Promise<void> => {
          return handleFormSubmit(e, "post", "/admin/awards");
        };
    const currentForm = formRef.current;
    currentForm.addEventListener("submit", listenForSubmit);

    return (): void => {
      currentForm.removeEventListener("submit", listenForSubmit);
    };
  }, [props.editing, props.award.award._id, handleFormSubmit]);

  return (
    <StyledTabWrapper>
      <AutomaticApprovalToggle
        approvedAutomatically={approvedAutomatically}
        handleApproveAutomaticallyChange={handleApproveAutomaticallyChange}
        submitting={submitting}
      />
      {!approvedAutomatically && (
        <Levels
          options={options}
          handleChange={handleChange}
          levels={levelState.levels}
          handleInputChange={handleInputChange}
          {...errorState}
          touched={touched}
          handleBlur={handleBlur}
          handleAddLevel={handleAddLevel}
          handleRemoveLevel={handleRemoveLevel}
          submitting={submitting}
        />
      )}
      {!approvedAutomatically && parseInt(props.pending_requests) > 0 && (
        <PendingAwardUpdates
          invalidApprovalProcess={props.invalid_approval_process}
          pendingRequestCount={parseInt(props.pending_requests)}
          resetPendingRequests={resetPendingRequests}
          setResetPendingRequests={setResetPendingRequests}
        />
      )}
    </StyledTabWrapper>
  );
}

ApproversTab.propTypes = {};

export default ApproversTab;
